import {Breadcrumb, HeaderTitle, LinkText} from '@/components/atoms';
import {PageHeader} from '@/components/molecules';
import PeacockDataTable from '@/components/organisms/PeacockDataTable';
import {usePointMultiplier} from '@/hooks/usePointMultiplier';
import {usePeacockDataTableConfig} from '@/hooks/usePeacockDataTableConfig';
import {ROUTES} from '@/utils/constants/routesConstants';
import {Header} from '@/utils/constants/header';
import {useNavigate} from 'react-router-dom';
import {useCallback, useMemo} from 'react';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';
import {resetPointMultiplier} from '@/store/slices/pointMultiplier';
import {useAppDispatch} from '@/store';
import './PointMultiplier/_pointMultiplier.scss';
import {getSelectedRowData} from '@/utils/commons';

const cellDataMapperObj = (navigate, isLoading) => ({
  multiplierName: {
    cellData: ({row: {original} = {}}) => (
      <LinkText
        text={original?.multiplierName}
        id={original?.multiplierName + ' ' + (original?.id || original?.uuid)}
        handleClick={() =>
          navigate(
            `${ROUTES.POINT_MULTIPLIER_VIEW.replace(':id', original?.id || original?.uuid)}`,
            {
              state: {
                id: original?.id,
                uuid: original?.uuid,
                formData: getSelectedRowData(original)
              }
            }
          )
        }
      />
    ),
    linkHandler: (row) => {
      if (row?.id || (row?.uuid && !isLoading)) {
        navigate(
          `${ROUTES.POINT_MULTIPLIER_VIEW.replace(':id', row?.id || row?.uuid)}`,
          {
            state: {
              id: row?.id,
              uuid: row?.uuid,
              formData: getSelectedRowData(row)
            }
          }
        );
      }
    },
    linkLabel: (row) => row.multiplierName || '\u2014'
  }
});

export const PointMultiplier = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    pointMultiplierData,
    pointMultiplierColConfigData,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    setSortingObj,
    setSearchArry,
    isLoading,
    isFetching,
    onDownloadBtnClick,
    handleResetAll,
    sortingObj
  } = usePointMultiplier();

  const columns = useMemo(
    () =>
      getCellDataMappedCols(
        cellDataMapperObj(navigate, isLoading),
        pointMultiplierColConfigData
      ),
    [navigate, isLoading, pointMultiplierColConfigData]
  );

  const {
    records,
    handleDownload,
    paginationConfig,
    sortingConfig,
    filterConfig,
    selectionConfig
  } = usePeacockDataTableConfig({
    data: pointMultiplierData,
    pageIndex,
    setPageIndex,
    setPageSize,
    setSortingObj,
    sortingObj,
    setSearchArry,
    handleResetAll,
    onDownloadBtnClick
  });

  const totalRecords = pointMultiplierData?.totalRecords || 0;

  const handleResetFormData = useCallback(() => {
    dispatch(resetPointMultiplier());
    navigate(ROUTES.POINT_MULTIPLIER_CREATE);
  }, [dispatch, navigate]);

  const toolbarConfig = useMemo(
    () => ({
      showDownload: true,
      onDownload: handleDownload,
      showCreateNew: true,
      onCreateNew: handleResetFormData,
      alwaysShowInToolbar: true
    }),
    [handleDownload, handleResetFormData]
  );

  return (
    <div className='pointMultiplierContent flex flex-col'>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: ROUTES.HOME,
              label: ''
            },

            {
              href: '',
              label: 'Point Multipliers'
            }
          ]
        }}
        id={'pm_bradcrumb'}
      />
      <PageHeader title='Point Multiplier' />
      <div
        className='relative'
        style={{
          backgroundColor: 'var(--color-background-layer-01)',
          paddingLeft: '16px'
        }}>
        <HeaderTitle
          headingLevel={Header.SectionTitle}
          title='Point Multiplier List'
          className='PointMultiplierDataTableHeading'
        />
        <div className='pointMultiplierDataTable'>
          <PeacockDataTable
            columns={columns}
            records={records}
            isLoading={isLoading}
            isFetching={isFetching}
            pageSize={pageSize}
            totalRecords={totalRecords}
            paginationConfig={paginationConfig}
            selectionConfig={selectionConfig}
            sortingConfig={sortingConfig}
            filterConfig={filterConfig}
            toolbarConfig={toolbarConfig}
          />
          {totalRecords > 0 && (
            <div className='text-md py-2 pr-4 text-right text-[color:var(--color-text-primary)]'>
              {totalRecords} total items
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
